import React, { useMemo } from "react";
import { useVenue } from "../VenueProvider";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { Section } from "../cuentas/shared";
import { getMonthRange } from "../../utils/dates";
import { variacion, resumenLeads, serieMensual } from "../../utils/reportes";
import { Kpi, fmtPct, ultimosMeses, labelMes, VALOR_COLOR } from "./shared";
import { LightBulbIcon, ChartBarIcon } from "@heroicons/react/20/solid";

// El embudo comercial: cuántos leads llegan, de dónde, y cuántos se cierran.
// Cohorte por fecha de llegada — "de los que llegaron en el período, cuántos
// van ganados" — para que los períodos sean comparables entre sí.
const ReporteComercial = ({ rango }) => {
  const {
    events,
    payments,
    refunds,
    costos,
    contactForms,
    calendarTimezone: tz,
  } = useVenue();

  const leads = useMemo(
    () => resumenLeads({ contactForms, desde: rango.from, hasta: rango.to, timeZone: tz }),
    [contactForms, rango, tz]
  );
  const leadsPrev = useMemo(
    () => resumenLeads({ contactForms, desde: rango.prev.from, hasta: rango.prev.to, timeZone: tz }),
    [contactForms, rango, tz]
  );

  const serie = useMemo(
    () =>
      serieMensual({
        meses: ultimosMeses(12),
        events,
        payments,
        refunds,
        costos,
        contactForms,
        timeZone: tz,
        getMonthRange,
      }).map((m) => ({ ...m, label: labelMes(m.key) })),
    [events, payments, refunds, costos, contactForms, tz]
  );

  const maxOrigen = leads.porOrigen[0]?.count || 0;
  const abiertos = leads.recibidos - leads.ganados - leads.perdidos;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi
          label="Leads recibidos"
          value={leads.recibidos}
          delta={variacion(leads.recibidos, leadsPrev.recibidos)}
        />
        <Kpi
          label="Ganados"
          value={leads.ganados}
          delta={variacion(leads.ganados, leadsPrev.ganados)}
          tone="text-emerald-600"
        />
        <Kpi
          label="Perdidos"
          value={leads.perdidos}
          delta={variacion(leads.perdidos, leadsPrev.perdidos)}
          invertir
          tone={leads.perdidos > 0 ? "text-red-600" : ""}
        />
        <Kpi
          label="Tasa de cierre"
          value={fmtPct(leads.tasaGanados)}
          delta={variacion(leads.tasaGanados, leadsPrev.tasaGanados)}
          hint={abiertos > 0 ? `${abiertos} siguen abiertos` : null}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section icon={LightBulbIcon} title="De dónde llegan">
          {leads.porOrigen.length === 0 ? (
            <p className="text-sm text-zinc-400">
              No llegaron leads en este período.
            </p>
          ) : (
            <div className="space-y-2.5">
              {leads.porOrigen.map((o) => (
                <div key={o.origen}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-zinc-600 dark:text-zinc-300">{o.origen}</span>
                    <span className="font-medium text-zinc-900 dark:text-white">
                      {o.count}
                      <span className="ml-1.5 text-xs font-normal text-zinc-400">
                        {leads.recibidos > 0
                          ? `${Math.round((o.count / leads.recibidos) * 100)}%`
                          : ""}
                      </span>
                    </span>
                  </div>
                  <div className="mt-0.5 h-1.5 w-full rounded-full bg-zinc-100 dark:bg-zinc-700/60 overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${maxOrigen > 0 ? Math.round((o.count / maxOrigen) * 100) : 0}%`,
                        backgroundColor: VALOR_COLOR,
                        opacity: 0.8,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
          <p className="mt-4 text-xs text-zinc-400">
            "Venueboard" son los formularios del sitio; el resto son los
            orígenes que capturas a mano en Leads.
          </p>
        </Section>

        <Section icon={ChartBarIcon} title="Leads por mes">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={serie} margin={{ top: 5, right: 5, left: 5, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={28} />
                <Tooltip
                  formatter={(v) => [v, "Leads"]}
                  labelStyle={{ fontSize: 12 }}
                  contentStyle={{ fontSize: 12, borderRadius: 8 }}
                />
                <Bar dataKey="leads" name="Leads" fill={VALOR_COLOR} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="mt-2 text-xs text-zinc-400">
            Los últimos 12 meses, sin importar el período elegido arriba. El
            detalle y el pipeline viven en la sección Leads; las visitas del
            sitio, en Analíticas.
          </p>
        </Section>
      </div>
    </div>
  );
};

export default ReporteComercial;
